import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ROLES, Role } from "@equipped/core/auth/roles";
import { AdminUser, GrantSpec, UserModeration } from "@equipped/core/backend/client";
import { isModerationActive } from "@equipped/core/billing/moderation";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";
import { AddUserDialog } from "../components/AddUserDialog";
import { SetPasswordDialog } from "../components/SetPasswordDialog";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PersonDetailDrawer } from "../components/PersonDetailDrawer";
import { useAdminFeatures } from "../features/AdminFeaturesContext";

/** Page-size choices stay within the worker's 200-row maximum. */
const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;
/** How long to wait after the last keystroke before the search box drives a
 * new server-side query — avoids firing a request per character. */
const SEARCH_DEBOUNCE_MS = 300;

function MembershipLink({ user }: { user: AdminUser }) {
  const { client, token } = useAuth();
  const moderationQuery = useQuery({
    queryKey: ["admin-moderation", user.id],
    queryFn: () => client!.adminModeration(token!, user.id),
    enabled: !!client && !!token,
  });
  const moderation = moderationQuery.data as UserModeration | null | undefined;
  const restricted = isModerationActive(moderation, Math.floor(Date.now() / 1000));
  const label = !restricted ? "Free" : moderation?.status === "banned" ? "Banned" : "Suspended";

  return (
    <Chip
      component={RouterLink}
      to="/membership"
      clickable
      size="small"
      variant="outlined"
      label={moderationQuery.isLoading ? "Loading…" : moderationQuery.isError ? "Unavailable" : label}
      color={
        moderationQuery.isError || moderationQuery.isLoading
          ? "default"
          : label === "Banned"
            ? "error"
            : label === "Suspended"
              ? "warning"
              : "primary"
      }
    />
  );
}

/**
 * People tab, re-skinned to MUI/Mantis (docs/337-ADMIN-MANTIS-MIGRATION.md v1) — the behavioral
 * reference is `specs/admin/screens/people.md` / `apps/admin/app/(tabs)/people.tsx`. Same v0 scope
 * as `apps/admin-web/src/pages/PeoplePage.tsx` (this build's "half the work" People tab mirrors
 * IT, not the RN admin's richer expand-a-row panel): the roster table (search + "show removed"),
 * role change, soft-delete/restore, and the three dialog actions (Set/reset password, Delete
 * permanently, Add user — docs/323/286). Every `useQuery`/`useMutation` here is lifted UNCHANGED
 * from `apps/admin-web`'s own page — only the returned JSX (MUI `Table` instead of CoreUI
 * `CTable`, `Dialog` instead of `CModal`, …) differs.
 *
 * **Member CRM / 360° detail drawer** (docs/384 idea #9) — a per-row "Details" button
 * opens `<PersonDetailDrawer>`, combining the roster identity with the existing per-user
 * history, devices, grants, and moderation reads. Each dataset has an independent TanStack
 * query and its own state; the drawer adds no endpoint or persisted profile data.
 *
 * Bulk email lookup and account actions now live on the dedicated `/bulk-operations`
 * route (docs/384 idea #11); this page keeps a direct toolbar link to that workflow.
 *
 * **Server-side cursor pagination** (docs/340-ADMIN-MANTIS-AND-API-IDEAS.md v1.1, closing
 * the v1 §4 "deferred to v1.1" note) — the roster used to load via the unbounded
 * `adminUsers()` (every account in one response, filtered client-side). It now loads a
 * one page at a time via `adminUsersPage()` (`useQuery`), with the search box
 * and a new role filter driving the server's own `q`/`role` params instead of an
 * in-memory `.filter()` — also mitigating docs/369 finding A2 (an unbounded admin list is
 * an OOM risk on a large/slow-scrolling account fleet). Changing the search text, role
 * filter, "Show removed", or page size starts a fresh page-1 fetch. Next stores the
 * server's `nextCursor`; Previous returns to the cursor saved for the preceding page.
 */
export function PeoplePage() {
  const { client, token } = useAuth();
  const { hasCapability } = useAdminFeatures();
  const queryClient = useQueryClient();
  // `rawQuery` is what the search box shows (updates every keystroke);
  // `query` is the debounced value that actually drives the server request.
  const [rawQuery, setRawQuery] = useState("");
  const [query, setQuery] = useState("");
  const [role, setRole] = useState<Role | "">("");
  const [showRemoved, setShowRemoved] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [passwordFor, setPasswordFor] = useState<AdminUser | null>(null);
  const [hardDeleteFor, setHardDeleteFor] = useState<AdminUser | null>(null);
  const [detailFor, setDetailFor] = useState<AdminUser | null>(null);
  const [busyUserIds, setBusyUserIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(rawQuery.trim());
      setPageCursors([undefined]);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [rawQuery]);

  const cursor = pageCursors[pageCursors.length - 1];
  const pageNumber = pageCursors.length;
  const usersQuery = useQuery({
    queryKey: ["admin-users-page", query, role, showRemoved, pageSize, cursor],
    queryFn: () =>
      client!.adminUsersPage(token!, {
        limit: pageSize,
        cursor,
        includeDeleted: showRemoved,
        q: query || undefined,
        role: role || undefined,
      }),
    enabled: !!client && !!token,
  });

  const invalidateUsers = () => queryClient.invalidateQueries({ queryKey: ["admin-users-page"] });
  const markUserBusy = (id: string) => setBusyUserIds((ids) => new Set(ids).add(id));
  const clearUserBusy = (id: string) =>
    setBusyUserIds((ids) => {
      const next = new Set(ids);
      next.delete(id);
      return next;
    });

  const setRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => client!.adminSetRole(token!, id, role),
    onMutate: ({ id }) => markUserBusy(id),
    onSuccess: invalidateUsers,
    onSettled: (_data, _error, { id }) => clearUserBusy(id),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => client!.adminDeleteUser(token!, id),
    onMutate: markUserBusy,
    onSuccess: invalidateUsers,
    onSettled: (_data, _error, id) => clearUserBusy(id),
  });
  const restoreMutation = useMutation({
    mutationFn: (id: string) => client!.adminRestoreUser(token!, id),
    onMutate: markUserBusy,
    onSuccess: invalidateUsers,
    onSettled: (_data, _error, id) => clearUserBusy(id),
  });
  const setPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => client!.adminSetUserPassword(token!, id, password),
    onMutate: ({ id }) => markUserBusy(id),
    onSuccess: () => {
      invalidateUsers();
      setPasswordFor(null);
    },
    onSettled: (_data, _error, { id }) => clearUserBusy(id),
  });
  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => client!.adminHardDeleteUser(token!, id),
    onMutate: markUserBusy,
    onSuccess: () => {
      invalidateUsers();
      setHardDeleteFor(null);
    },
    onSettled: (_data, _error, id) => clearUserBusy(id),
  });
  const addUserMutation = useMutation({
    mutationFn: (input: { email: string; firstName?: string; lastName?: string; grants: GrantSpec[] }) =>
      client!.adminCreateUser(token!, input),
    onSuccess: () => {
      invalidateUsers();
      setShowAddUser(false);
    },
  });

  // Server-side search/filter (docs/340 v1.1) — the displayed rows are exactly the
  // current server page, never a client-side re-filter of an unbounded roster.
  const filtered = usersQuery.data?.users ?? [];
  const nextCursor = usersQuery.data?.nextCursor ?? null;

  const columnHelper = createColumnHelper<AdminUser>();
  const columns = useMemo(
    () => [
      columnHelper.accessor((u) => [u.firstName, u.lastName].filter(Boolean).join(" ") || "—", {
        id: "name",
        header: "Name",
      }),
      columnHelper.accessor("email", { header: "Email" }),
      columnHelper.display({
        id: "role",
        header: "Role",
        cell: ({ row }) => {
          const u = row.original;
          if (u.deletedAt) return <Typography color="text.secondary">—</Typography>;
          // docs/340-ADMIN-MANTIS-AND-API-IDEAS.md §3 — `setRoleMutation`/`deleteMutation`/
          // `restoreMutation` are each ONE shared mutation for every row, so `.isPending`
          // alone used to disable the SAME control on every user while one was in flight.
          // Scope all controls to THIS row. The Set supports overlapping requests on
          // different users without re-enabling an earlier row when a later request starts.
          const rowPending = busyUserIds.has(u.id);
          return hasCapability("users.setRole") ? (
            <TextField
              select
              size="small"
              value={u.role}
              disabled={rowPending}
              onChange={(e) => setRoleMutation.mutate({ id: u.id, role: e.target.value as Role })}
              sx={{ minWidth: 120 }}
            >
              {ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography variant="body2">{u.role}</Typography>
          );
        },
      }),
      columnHelper.display({
        id: "membership",
        header: "Membership",
        cell: ({ row }) => <MembershipLink user={row.original} />,
      }),
      columnHelper.display({
        id: "status",
        header: "Status",
        cell: ({ row }) => {
          const u = row.original;
          if (u.deletedAt) return <Chip label="Removed" color="error" size="small" />;
          if (u.provisionedAt && !u.claimedAt) return <Chip label="Pending sign-in" color="warning" size="small" />;
          return <Chip label="Active" color="success" size="small" />;
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const u = row.original;
          const rowPending = busyUserIds.has(u.id);
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Button size="small" variant="outlined" disabled={rowPending} onClick={() => setDetailFor(u)}>
                Details
              </Button>
              {hasCapability("users.setPassword") ? (
                <Button size="small" variant="outlined" color="secondary" disabled={rowPending} onClick={() => setPasswordFor(u)}>
                  Set password
                </Button>
              ) : null}
              {u.deletedAt ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  disabled={rowPending}
                  onClick={() => restoreMutation.mutate(u.id)}
                >
                  Restore
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={rowPending}
                  onClick={() => deleteMutation.mutate(u.id)}
                >
                  Delete
                </Button>
              )}
              <Button size="small" variant="outlined" color="error" disabled={rowPending} onClick={() => setHardDeleteFor(u)}>
                Delete permanently
              </Button>
            </Box>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      busyUserIds,
      hasCapability,
    ],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (u) => u.id,
  });

  return (
    <>
      <MainCard
        title="Users & access"
        secondary={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button component={RouterLink} to="/bulk-operations" variant="outlined" size="small">
              Bulk operations
            </Button>
            <Button variant="contained" size="small" onClick={() => setShowAddUser(true)}>
              ＋ Add user
            </Button>
          </Box>
        }
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name or email…"
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            sx={{ maxWidth: 320 }}
          />
          <TextField
            select
            size="small"
            label="Role"
            value={role}
            onChange={(e) => {
              setRole(e.target.value as Role | "");
              setPageCursors([undefined]);
            }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All roles</MenuItem>
            {ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={showRemoved}
                onChange={(e) => {
                  setShowRemoved(e.target.checked);
                  setPageCursors([undefined]);
                }}
              />
            }
            label="Show removed"
          />
        </Box>

        {usersQuery.isError ? <Alert severity="error">{(usersQuery.error as Error).message}</Alert> : null}

        {usersQuery.isLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 0 }}>
            {query || role ? "No people match your search." : "No people yet."}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableCell key={header.id}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} hover>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!usersQuery.isLoading && !usersQuery.isError && (filtered.length > 0 || pageNumber > 1) ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mt: 2,
            }}
          >
            <TextField
              select
              size="small"
              label="Rows per page"
              value={pageSize}
              disabled={usersQuery.isFetching}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageCursors([undefined]);
              }}
              sx={{ minWidth: 140 }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              Page {pageNumber} · {filtered.length} {filtered.length === 1 ? "person" : "people"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={pageNumber === 1 || usersQuery.isFetching}
                onClick={() => setPageCursors((current) => current.slice(0, -1))}
              >
                Previous
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!nextCursor || usersQuery.isFetching}
                onClick={() => {
                  if (nextCursor) setPageCursors((current) => [...current, nextCursor]);
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        ) : null}
      </MainCard>

      <AddUserDialog
        open={showAddUser}
        busy={addUserMutation.isPending}
        error={addUserMutation.isError ? (addUserMutation.error as Error).message : null}
        onClose={() => setShowAddUser(false)}
        onSubmit={(input) => addUserMutation.mutate(input)}
      />

      <SetPasswordDialog
        open={!!passwordFor}
        userEmail={passwordFor?.email ?? null}
        busy={setPasswordMutation.isPending}
        error={setPasswordMutation.isError ? (setPasswordMutation.error as Error).message : null}
        onClose={() => setPasswordFor(null)}
        onSubmit={(password) => {
          if (passwordFor) setPasswordMutation.mutate({ id: passwordFor.id, password });
        }}
      />

      <ConfirmDialog
        open={!!hardDeleteFor}
        title="Permanently delete this person?"
        body={`This removes ${hardDeleteFor?.email ?? "this account"}, their sessions, and grants outright and frees the email for reuse. There is no undo.`}
        confirmLabel="Delete permanently"
        busy={hardDeleteMutation.isPending}
        error={hardDeleteMutation.isError ? (hardDeleteMutation.error as Error).message : null}
        onCancel={() => setHardDeleteFor(null)}
        onConfirm={() => {
          if (hardDeleteFor) hardDeleteMutation.mutate(hardDeleteFor.id);
        }}
      />

      <PersonDetailDrawer open={!!detailFor} user={detailFor} onClose={() => setDetailFor(null)} />

    </>
  );
}
